import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator, setPersistence, browserSessionPersistence, browserLocalPersistence } from "firebase/auth";
import { initializeFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getAnalytics, logEvent, isSupported } from "firebase/analytics";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { devLog, FEATURES } from '../utils/environment';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};



const app = initializeApp(firebaseConfig);

// Initialize App Check (bot protection)
if (process.env.REACT_APP_USE_EMULATORS !== "true") {
  // Production: Use reCAPTCHA v3
  if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
    try {
      initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
        isTokenAutoRefreshEnabled: true
      });
    } catch (error) {
      // App Check initialization failed - continue without it
    }
  }
} else {
  // Development: Use debug token
  if (typeof window !== 'undefined') {
    window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
}

const auth = getAuth(app);
// CRITICAL: Always use session persistence in production for security
// Only use local persistence on localhost for development
const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
if (FEATURES.SKIP_EMAIL_VERIFICATION && isLocalhost) {
  // Development on localhost: persist across tabs for easier testing
  setPersistence(auth, browserLocalPersistence).catch(() => {});
  devLog.info('DEVELOPMENT MODE: Using localStorage persistence for cross-tab auth');
} else {
  // Production OR non-localhost: session only (more secure, auto-logout on close)
  setPersistence(auth, browserSessionPersistence).catch(() => {});
  devLog.info('PRODUCTION MODE: Using session persistence for security');
}
// Force long-polling to mitigate networks / extensions blocking WebChannel (400 Bad Request issues)
const db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
  useFetchStreams: false
});
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Initialize Google Analytics (only in production)
let analytics = null;
if (FEATURES.ENABLE_PERFORMANCE_MONITORING) {
  isSupported().then(yes => {
    if (yes) {
      analytics = getAnalytics(app);
      devLog.info('Google Analytics initialized');
    }
  }).catch(() => {
    devLog.warn('Google Analytics not supported in this environment');
  });
}

// Connect to emulators in development mode
if (FEATURES.USE_FIREBASE_EMULATORS) {
  devLog.info('DEVELOPMENT MODE: Connecting to Firebase Emulators');
  
  try {
    // Connect Auth emulator
    connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
    devLog.success('Connected to Auth Emulator (http://127.0.0.1:9099)');
  } catch (error) {
    devLog.warn('Auth emulator already connected or unavailable');
  }
  
  try {
    // Connect Firestore emulator
    connectFirestoreEmulator(db, "127.0.0.1", 8080);
    devLog.success('Connected to Firestore Emulator (127.0.0.1:8080)');
  } catch (error) {
    devLog.warn('Firestore emulator already connected or unavailable');
  }
  
  try {
    // Connect Storage emulator
    connectStorageEmulator(storage, "127.0.0.1", 9199);
    devLog.success('Connected to Storage Emulator (127.0.0.1:9199)');
  } catch (error) {
    devLog.warn('Storage emulator already connected or unavailable');
  }
  
  devLog.info('All emulator connections attempted - using LOCAL data only!');
} else {
  devLog.info('PRODUCTION MODE: Using live Firebase services');
}

export { auth, db, analytics, logEvent };
